import type { FlashCellsParams, GridApi, IRowNode } from 'ag-grid-community';
import { compact, uniqBy } from 'lodash-es';

import { useMemo } from 'react';
import { useMixpanel } from '../../../contexts';
import { useCallbackRef } from '../../../hooks';
import { MixpanelEvent } from '../../../tokens';
import { useBlotterTableContext } from '../BlotterTableContext';
import { getCustomColDefProperties } from '../columns';
import type {
  BlotterTableRow,
  ExportDataAsCsvParams,
  ExportDataAsExcelParams,
  ExportDataParams,
  TalosGetSheetDataForExcelParams,
  UseBlotterTableUtilitiesOutput,
} from '../types';
import type { UsePersistedBlotterTable } from '../usePersistedBlotterTable';
import {
  cellCsvSafety,
  expandAllNodesInGroup,
  expandParentsEnsureVisibleAndFlash,
  getAllParentsOfNodeInclusive,
  getParamsFormatted,
  isGridApiReady,
  type ParamsFormattedOptions,
} from '../utils';

export function useBlotterTableUtilities<R>(
  api?: GridApi,
  _persistence?: UsePersistedBlotterTable
): UseBlotterTableUtilitiesOutput<R> {
  const mixpanel = useMixpanel();

  const addRow = useCallbackRef((data?: R) => {
    if (!isGridApiReady(api)) {
      return;
    }
    api.applyTransactionAsync({ add: [{ ...data }] });
  });
  const getRows = useCallbackRef(() => {
    const rows: BlotterTableRow<R>[] = [];
    if (isGridApiReady(api)) {
      api.forEachNode(node => {
        rows.push({
          data: node.data,
          setData: newData => node.setData(newData),
          remove: () => api.applyTransactionAsync({ remove: [node.data] }),
          setSelected: (selected: boolean) => {
            node.setSelected(selected);
          },
        });
      });
    }
    return rows;
  });
  const getSelectedRows = useCallbackRef(() => {
    if (isGridApiReady(api)) {
      const selectedNodes = api.getSelectedNodes();
      const selectedData: BlotterTableRow<R>[] = selectedNodes.map(node => ({
        data: node.data,
        setData: newData => node.setData(newData),
        remove: () => {
          if (!isGridApiReady(api)) {
            return;
          }
          return api.applyTransactionAsync({ remove: [node.data] });
        },
        setSelected: (selected: boolean) => {
          node.setSelected(selected);
        },
      }));
      return selectedData;
    }
    return [];
  });

  const getRowsAfterFilter = useCallbackRef(() => {
    if (!isGridApiReady(api)) {
      return [];
    }

    const rows: BlotterTableRow<R>[] = [];
    api.forEachNodeAfterFilter(node => {
      rows.push({
        data: node.data,
        setData: newData => node.setData(newData),
        remove: () => {
          if (!isGridApiReady(api)) {
            return;
          }
          return api.applyTransactionAsync({ remove: [node.data] });
        },
        setSelected: (selected: boolean) => {
          node.setSelected(selected);
        },
      });
    });

    return rows;
  });

  const getColumnKeysToUseForExport = useCallbackRef(
    ({ includeHiddenColumns, ignoredColIds, ignoreColumn, columnKeys }: ExportDataParams) => {
      let columns = api?.getAllGridColumns() ?? []; // getAllGridColumns returns all columns in the order they appear in the blotter
      if (!includeHiddenColumns) {
        columns = columns.filter(c => c.isVisible());
      }

      const columnKeysToUse =
        columnKeys ??
        columns.filter(column => {
          const colDef = column.getColDef();

          const customColDef = getCustomColDefProperties(colDef);
          if (customColDef && customColDef.exportable === false) {
            return false;
          }

          if (colDef.headerName === '') {
            return false;
          }

          if (ignoredColIds && ignoredColIds.has(column.getColId())) {
            return false;
          }

          if (ignoreColumn && ignoreColumn(colDef)) {
            return false;
          }

          return true;
        });

      return columnKeysToUse;
    }
  );

  const { settings } = useBlotterTableContext();
  const paramsFormattedOptions = useMemo(
    () =>
      ({
        exportDatesRaw: settings?.exportDatesRaw ?? false,
      }) satisfies ParamsFormattedOptions,
    [settings?.exportDatesRaw]
  );

  const defaultExportParams = useMemo(
    () => ({
      skipRowGroups: true,
      skipPinnedTop: true,
      skipPinnedBottom: true,
      suppressQuotes: false,
      columnSeparator: ',',
    }),
    []
  );

  const exportDataAsExcel = useCallbackRef((exportParams: ExportDataAsExcelParams) => {
    const columnKeysToUse = getColumnKeysToUseForExport(exportParams);

    return api?.exportDataAsExcel({
      ...defaultExportParams,
      columnKeys: columnKeysToUse,
      processCellCallback: params =>
        getParamsFormatted(params, 'Excel', {
          // if exportParams are passed in, they take precedence over the settings params
          exportDatesRaw: exportParams.exportDatesRaw ?? paramsFormattedOptions.exportDatesRaw,
        }),
      ...exportParams,
    });
  });

  const getSheetDataForExcel = useCallbackRef((sheetParams: TalosGetSheetDataForExcelParams) => {
    const columnKeysToUse = getColumnKeysToUseForExport(sheetParams);

    return api?.getSheetDataForExcel({
      ...defaultExportParams,
      columnKeys: columnKeysToUse,
      processCellCallback: params =>
        getParamsFormatted(params, 'CSV', {
          // if sheetParams are passed in, they take precedence over the settings params
          exportDatesRaw: sheetParams.exportDatesRaw ?? paramsFormattedOptions.exportDatesRaw,
        }),
      ...sheetParams,
    });
  });

  const exportDataAsCSV = useCallbackRef((exportParams: ExportDataAsCsvParams) => {
    const columnKeysToUse = getColumnKeysToUseForExport(exportParams);

    return api?.exportDataAsCsv({
      ...defaultExportParams,
      columnKeys: columnKeysToUse,
      processCellCallback: params => {
        const cellContent = getParamsFormatted(params, 'CSV', {
          // if exportParams are passed in, they take precedence over the settings params
          exportDatesRaw: exportParams.exportDatesRaw ?? paramsFormattedOptions.exportDatesRaw,
        });
        const safeCsvCellContent = cellCsvSafety(cellContent);
        return safeCsvCellContent;
      },
      ...exportParams,
    });
  });

  const getDataAsCSV = useCallbackRef((exportParams: ExportDataAsCsvParams) => {
    const columnKeysToUse = getColumnKeysToUseForExport(exportParams);
    return api?.getDataAsCsv({
      ...defaultExportParams,
      columnKeys: columnKeysToUse,
      processCellCallback: params =>
        getParamsFormatted(params, 'CSV', {
          // if exportParams are passed in, they take precedence over the settings params
          exportDatesRaw: exportParams.exportDatesRaw ?? paramsFormattedOptions.exportDatesRaw,
        }),
      ...exportParams,
    });
  });

  /**
   * Expand a group row.
   * The recursively param, defaulting to true, tells the function to open any intermediary group rows as well
   */
  const expandGroupRow = useCallbackRef((nodeKey: string, recursively = true) => {
    if (!isGridApiReady(api)) {
      return;
    }

    api.forEachNode((node: IRowNode) => {
      if (node.group && node.key === nodeKey) {
        node.setExpanded(true);

        // Recursively open all parent nodes up to the root level
        if (recursively) {
          let workingNode = node;
          while (workingNode.level >= 0) {
            if (workingNode.parent == null) {
              break;
            }
            workingNode = workingNode.parent;
            workingNode.setExpanded(true);
          }
        }
      }
    });
  });

  const scrollToRow = useCallbackRef((...args: Parameters<GridApi<R>['ensureNodeVisible']>) => {
    if (!isGridApiReady(api)) {
      return;
    }

    api.ensureNodeVisible(...args);
  });

  const ensureRowVisibleById = useCallbackRef(
    (...args: Parameters<UseBlotterTableUtilitiesOutput<R>['ensureRowVisibleById']>): void => {
      const [rowId, options] = args;
      if (!isGridApiReady(api) || !rowId) {
        return;
      }

      const timeoutMs = options?.timeout ?? 2000;
      const position = options?.position ?? 'middle';
      const highlight = options?.highlight ?? false;

      const startTime = Date.now();
      const pollInterval = 50;

      const check = () => {
        if (!isGridApiReady(api) || Date.now() - startTime >= timeoutMs) {
          return;
        }

        const node = api.getRowNode(rowId);
        if (node) {
          if (highlight) {
            expandParentsEnsureVisibleAndFlash(api, [node], { position });
          } else {
            getAllParentsOfNodeInclusive(node).forEach(n => n.setExpanded(true));
            api.ensureNodeVisible(node, position);
          }
          return;
        }
        setTimeout(check, pollInterval);
      };

      setTimeout(check, 0);
    }
  );

  const scrollVerticallyToColumn = useCallbackRef((...args: Parameters<GridApi<R>['ensureColumnVisible']>) => {
    if (!isGridApiReady(api)) {
      return;
    }

    api.ensureColumnVisible(...args);
  });

  const expandAllGroups = useCallbackRef(() => {
    if (!isGridApiReady(api)) {
      return;
    }
    mixpanel.track(MixpanelEvent.ExpandAllRows);

    api.expandAll();
  });

  const collapseAllGroups = useCallbackRef(() => {
    if (!isGridApiReady(api)) {
      return;
    }
    mixpanel.track(MixpanelEvent.CollapseAllRows);

    api.collapseAll();
  });

  const collapseAllLevelsGreaterThan = useCallbackRef((level: number) => {
    if (!isGridApiReady(api)) {
      return;
    }
    api.forEachNode(node => {
      if (node.level > level) {
        api.setRowNodeExpanded(node, false);
      }
    });
  });

  const setRowGroupColumns = useCallbackRef((...[columnsOrColIds]: Parameters<GridApi['setRowGroupColumns']>) => {
    if (!isGridApiReady(api)) {
      return;
    }

    // Grab the column object if there are any colIds passed in so we have a uniform array going forward
    const columns = columnsOrColIds
      .map(item => {
        if (typeof item === 'string') {
          return api.getColumn(item);
        }

        return item;
      })
      .compact();
    api.setRowGroupColumns(columns);
  });

  const addRowGroupColumns = useCallbackRef((colIds: string[]) => {
    if (!isGridApiReady(api)) {
      return;
    }

    // Grab the existing grouped columns, join with the new ones we're adding, and pass to the set function above
    const currentRowGroupColumns = api.getRowGroupColumns();
    const addedRowGroupColumns = colIds.map(colId => api.getColumn(colId)).compact();
    const uniqueNewRowGroupColumns = uniqBy([...currentRowGroupColumns, ...addedRowGroupColumns], c => c.getColId());
    setRowGroupColumns(uniqueNewRowGroupColumns);
  });

  const removeRowGroupColumns = useCallbackRef((colIds: string[]) => {
    if (!isGridApiReady(api)) {
      return;
    }

    api.removeRowGroupColumns(colIds);
  });

  const getRowGroupColumnIds = useCallbackRef(() => {
    if (!isGridApiReady(api)) {
      return new Set<string>();
    }

    return new Set(compact(api.getRowGroupColumns().map(c => c.getColId())));
  });

  const setColumnsVisible: GridApi['setColumnsVisible'] = useCallbackRef((...args) => {
    if (!isGridApiReady(api)) {
      return;
    }
    api.setColumnsVisible(...args);
  });

  const flashCells = useCallbackRef((params: FlashCellsParams) => {
    if (!isGridApiReady(api)) {
      return;
    }

    api.flashCells(params);
  });

  /**
   * Highlight rows in the grid based on passed ids. This will expand all the parent nodes of the rows to be highlighted.
   * - If expandChildren is true, it will also expand all the children of the group nodes that are being highlighted.
   */
  const highlightRows = useCallbackRef((rowIDs: string[], expandChildren?: boolean) => {
    if (!isGridApiReady(api)) {
      return;
    }

    const nodes = compact(rowIDs.map(id => api.getRowNode(id)));
    if (nodes.length === 0) {
      return;
    }

    for (const node of nodes) {
      if (node.group && expandChildren) {
        expandAllNodesInGroup(node);
      }
    }

    expandParentsEnsureVisibleAndFlash(api, nodes, { position: 'middle' });
  });

  const highlightGroupRow = useCallbackRef((groupingKey: string) => {
    return highlightRowByKey([groupingKey], true);
  });

  const highlightRowByKey = useCallbackRef((nodeKeysToSearch: string[], groupsOnly?: boolean) => {
    if (!isGridApiReady(api)) {
      return;
    }

    // first we have to find some groupRow that matches this key
    let referencedNode: IRowNode | undefined = undefined;
    api.forEachNodeAfterFilter(n => {
      if (referencedNode != null || (groupsOnly && !n.group) || !n.key) {
        return;
      }
      if (nodeKeysToSearch.includes(n.key)) {
        referencedNode = n;
        // there's no way to break out of this loop afaik
      }
    });

    if (!referencedNode) {
      return;
    }

    const rowID = (referencedNode as IRowNode).id;
    if (rowID) {
      highlightRows([rowID], true);
    }
  });

  const selectRows = useCallbackRef((rowIDs: string[]) => {
    if (!isGridApiReady(api)) {
      return;
    }

    api.deselectAll();
    rowIDs.forEach(id => api.getRowNode(id)?.setSelected(true));
  });

  const selectAllRows = useCallbackRef(() => {
    if (!isGridApiReady(api)) {
      return;
    }

    api.selectAll();
  });

  const refreshClientSideRowModel = useCallbackRef(() => {
    if (!isGridApiReady(api)) {
      return;
    }

    api.refreshClientSideRowModel();
  });

  const hidePopupMenu = useCallbackRef(() => {
    api?.hidePopupMenu();
  });

  const getAllDisplayedColumns = useCallbackRef(() => {
    if (!isGridApiReady(api)) {
      return [];
    }
    return api?.getAllDisplayedColumns();
  });

  return {
    gridApi: api,
    addRow,
    getRows,
    getRowsAfterFilter,
    getSelectedRows,
    exportDataAsCSV,
    exportDataAsExcel,
    getDataAsCSV,
    expandGroupRow,
    scrollToRow,
    ensureRowVisibleById,
    scrollVerticallyToColumn,
    expandAllGroups,
    collapseAllGroups,
    collapseAllLevelsGreaterThan,
    setRowGroupColumns,
    addRowGroupColumns,
    removeRowGroupColumns,
    getRowGroupColumnIds,
    setColumnsVisible,
    flashCells,
    highlightRows,
    highlightGroupRow,
    highlightRowByKey,
    selectRows,
    selectAllRows,
    refreshClientSideRowModel,
    getSheetDataForExcel,
    hidePopupMenu,
    getAllDisplayedColumns,
  };
}
